/**
 * Ice Spear Abyss Effect (滅劍羅淵)
 * A-tier special effect with directional ice spears
 */

export interface Position {
  x: number;
  y: number;
}

interface IceSpear {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  life: number;
  currentLife: number;
  trail: Array<{x: number, y: number}>;
  scale: number;
  targetX: number;
  targetY: number;
}

interface IceShard {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  life: number;
  currentLife: number;
}

interface ShockRing {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  life: number;
  currentLife: number;
}

export class IceSpearEffect {
  private x: number;
  private y: number;
  private targetPositions: Position[];
  private iceSpears: IceSpear[] = [];
  private iceShards: IceShard[] = [];
  private shockRings: ShockRing[] = [];
  private duration: number = 1.5;
  private currentTime: number = 0;
  private isComplete: boolean = false;
  private screenShake: number = 0;
  private frostCoreTime: number = 0;
  private iceSpearSprite: HTMLCanvasElement;
  public dead: boolean = false;

  constructor(x: number, y: number, targetPositions: Position[] = []) {
    this.x = x;
    this.y = y;
    this.targetPositions = targetPositions.length > 0 ? targetPositions : [{ x, y }];
    this.iceSpearSprite = this.createIceSpearSprite();
    this.initializeIceSpears();
  }

  private createIceSpearSprite(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = 26;
    canvas.height = 140;
    const ctx = canvas.getContext('2d')!;

    // Outer frost glow
    const glowGradient = ctx.createLinearGradient(0, 0, 0, 140);
    glowGradient.addColorStop(0, 'rgba(190,230,255,0.00)');
    glowGradient.addColorStop(0.1, 'rgba(190,230,255,0.20)');
    glowGradient.addColorStop(0.9, 'rgba(190,230,255,0.20)');
    glowGradient.addColorStop(1, 'rgba(190,230,255,0.00)');
    ctx.fillStyle = glowGradient;
    ctx.fillRect(0, 0, 26, 140);

    // Ice blade main body
    ctx.fillStyle = 'rgba(190,230,255,0.95)';
    ctx.beginPath();
    ctx.moveTo(13, 0);
    ctx.lineTo(20, 100);
    ctx.lineTo(13, 140);
    ctx.lineTo(6, 100);
    ctx.closePath();
    ctx.fill();

    // Core highlight
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.fillRect(12, 12, 2, 112);

    return canvas;
  }

  private initializeIceSpears(): void {
    // Create 2-3 ice spears per target for precise strikes
    for (const target of this.targetPositions) {
      const spearsPerTarget = Math.random() < 0.4 ? 2 : 3; // 60% chance for 3 spears, 40% for 2

      for (let i = 0; i < spearsPerTarget; i++) {
        // Spawn positions around the target with some spread
        const angle = Math.random() * Math.PI * 2;
        const distance = 350 + Math.random() * 200;

        const sx = target.x + Math.cos(angle) * distance;
        const sy = target.y + Math.sin(angle) * distance - 150; // Bias upward

        const dx = target.x - sx;
        const dy = target.y - sy;
        const totalDistance = Math.sqrt(dx * dx + dy * dy) || 1;

        const speed = 900 + Math.random() * 400; // Fast ice spears

        this.iceSpears.push({
          x: sx,
          y: sy,
          vx: (dx / totalDistance) * speed,
          vy: (dy / totalDistance) * speed,
          rotation: Math.atan2(dy, dx) + Math.PI / 2, // Add 90° for sprite orientation
          life: 0.45 + Math.random() * 0.45,
          currentLife: 0,
          trail: [],
          scale: 0.8 + Math.random() * 0.4,
          targetX: target.x,
          targetY: target.y,
        });
      }
    }
  }

  private spawnIceShards(x: number, y: number, count: number = 22): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 300 + Math.random() * 600;

      this.iceShards.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 16,
        life: 0.18 + Math.random() * 0.2,
        currentLife: 0,
      });
    }
  }

  private addShockRing(x: number, y: number): void {
    this.shockRings.push({
      x,
      y,
      radius: 0,
      maxRadius: 200,
      life: 0.45,
      currentLife: 0,
    });
  }

  public update(deltaTime: number): void {
    this.currentTime += deltaTime;

    if (this.currentTime >= this.duration) {
      this.isComplete = true;
      this.dead = true;
      return;
    }

    this.screenShake = Math.max(0, this.screenShake - deltaTime * 16);
    this.frostCoreTime += deltaTime * 3;

    // Spawn new ice spears during first 60% of effect
    const progress = this.currentTime / this.duration;
    if (progress < 0.6 && Math.random() < 18 * deltaTime) {
      // Randomly add a few more spears for intensity
      if (Math.random() < 0.5) {
        const extraSpears = Math.random() < 0.5 ? 2 : 3;
        const randomTarget = this.targetPositions[Math.floor(Math.random() * this.targetPositions.length)];
        if (randomTarget) {
          for (let i = 0; i < extraSpears; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = 300 + Math.random() * 250;

            const sx = randomTarget.x + Math.cos(angle) * distance;
            const sy = randomTarget.y + Math.sin(angle) * distance - 100;

            const dx = randomTarget.x - sx;
            const dy = randomTarget.y - sy;
            const totalDistance = Math.sqrt(dx * dx + dy * dy) || 1;

            const speed = 900 + Math.random() * 400;

            this.iceSpears.push({
              x: sx,
              y: sy,
              vx: (dx / totalDistance) * speed,
              vy: (dy / totalDistance) * speed,
              rotation: Math.atan2(dy, dx) + Math.PI / 2,
              life: 0.45 + Math.random() * 0.45,
              currentLife: 0,
              trail: [],
              scale: 0.8 + Math.random() * 0.4,
              targetX: randomTarget.x,
              targetY: randomTarget.y,
            });
          }
        }
      }
    }

    this.updateIceSpears(deltaTime);
    this.updateIceShards(deltaTime);
    this.updateShockRings(deltaTime);
  }

  private updateIceSpears(deltaTime: number): void {
    for (let i = this.iceSpears.length - 1; i >= 0; i--) {
      const spear = this.iceSpears[i];
      if (!spear) {continue;}

      spear.currentLife += deltaTime;

      spear.x += spear.vx * deltaTime;
      spear.y += spear.vy * deltaTime;

      // Add to trail
      spear.trail.push({ x: spear.x, y: spear.y });
      if (spear.trail.length > 12) {
        spear.trail.shift();
      }

      // Check if spear should explode (lifespan or near target)
      const distanceToTarget = Math.sqrt(
        (spear.x - spear.targetX) ** 2 + (spear.y - spear.targetY) ** 2,
      );

      if (spear.currentLife > spear.life || distanceToTarget < 25) {
        this.spawnIceShards(spear.x, spear.y, 28);
        this.addShockRing(spear.x, spear.y);
        this.screenShake = Math.min(8, this.screenShake + 2.5);
        this.iceSpears.splice(i, 1);
      }
    }
  }

  private updateIceShards(deltaTime: number): void {
    for (let i = this.iceShards.length - 1; i >= 0; i--) {
      const shard = this.iceShards[i];
      if (!shard) {continue;}

      shard.currentLife += deltaTime;

      if (shard.currentLife > shard.life) {
        this.iceShards.splice(i, 1);
        continue;
      }

      shard.vy += 900 * deltaTime; // Gravity
      shard.x += shard.vx * deltaTime;
      shard.y += shard.vy * deltaTime;
      shard.rotation += shard.rotationSpeed * deltaTime;
    }
  }

  private updateShockRings(deltaTime: number): void {
    for (let i = this.shockRings.length - 1; i >= 0; i--) {
      const ring = this.shockRings[i];
      if (!ring) {continue;}

      ring.currentLife += deltaTime * 2.2;

      if (ring.currentLife >= ring.life) {
        this.shockRings.splice(i, 1);
        continue;
      }

      ring.radius = 18 + (ring.currentLife / ring.life) * ring.maxRadius;
    }
  }

  public draw(ctx: CanvasRenderingContext2D): void {
    if (this.isComplete) {return;}

    const age = this.currentTime / this.duration;
    const fadeIn = Math.min(1, age / 0.12);
    const fadeOut = Math.min(1, (this.duration - this.currentTime) / 0.25);
    const alpha = Math.min(fadeIn, fadeOut);

    ctx.save();

    // Apply screen shake
    if (this.screenShake > 0) {
      const shakeX = (Math.random() - 0.5) * this.screenShake * 2;
      const shakeY = (Math.random() - 0.5) * this.screenShake * 2;
      ctx.translate(shakeX, shakeY);
    }

    this.drawFrostCore(ctx, alpha);
    this.drawIceSpears(ctx, alpha);
    this.drawShockRings(ctx, alpha);
    this.drawIceShards(ctx, alpha);

    ctx.restore();
  }

  private drawFrostCore(ctx: CanvasRenderingContext2D, alpha: number): void {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    const radius = 140 + 40 * Math.sin(this.frostCoreTime * 6);
    const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, radius);
    gradient.addColorStop(0, `rgba(220,240,255,${0.5 * alpha})`);
    gradient.addColorStop(1, 'rgba(220,240,255,0)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(this.x, this.y, radius, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private drawIceSpears(ctx: CanvasRenderingContext2D, alpha: number): void {
    for (const spear of this.iceSpears) {
      // Draw frost trail
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';

      for (let k = 0; k < spear.trail.length - 1; k++) {
        const current = spear.trail[k];
        const next = spear.trail[k + 1];
        if (!current || !next) {continue;}

        const progress = k / (spear.trail.length - 1);
        const width = 18 * (1 - progress);

        ctx.globalAlpha = alpha * 0.35 * (1 - progress);
        ctx.strokeStyle = 'rgba(150,210,255,0.40)';
        ctx.lineCap = 'round';
        ctx.lineWidth = width;

        ctx.beginPath();
        ctx.moveTo(current.x, current.y);
        ctx.lineTo(next.x, next.y);
        ctx.stroke();
      }

      // Draw ice spear sprite
      ctx.globalAlpha = alpha;
      ctx.translate(spear.x, spear.y);
      ctx.rotate(spear.rotation);
      const scale = spear.scale * (0.8 + Math.min(0.7, spear.currentLife * 1.1));
      ctx.scale(scale, scale);
      ctx.drawImage(this.iceSpearSprite, -13, -20);

      ctx.restore();
    }
  }

  private drawIceShards(ctx: CanvasRenderingContext2D, alpha: number): void {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    for (const shard of this.iceShards) {
      const life = 1 - (shard.currentLife / shard.life);
      ctx.globalAlpha = alpha * life;

      ctx.save();
      ctx.translate(shard.x, shard.y);
      ctx.rotate(shard.rotation);

      ctx.strokeStyle = 'rgba(210,240,255,0.95)';
      ctx.lineWidth = 2;

      ctx.beginPath();
      ctx.moveTo(-3, 0);
      ctx.lineTo(3, 0);
      ctx.moveTo(0, -3);
      ctx.lineTo(0, 3);
      ctx.stroke();

      ctx.restore();
    }

    ctx.restore();
  }

  private drawShockRings(ctx: CanvasRenderingContext2D, alpha: number): void {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    for (const ring of this.shockRings) {
      const life = 1 - (ring.currentLife / ring.life);
      ctx.globalAlpha = alpha * life;
      ctx.strokeStyle = 'rgba(180,220,255,0.70)';
      ctx.lineWidth = 2 + 6 * life;

      ctx.beginPath();
      ctx.arc(ring.x, ring.y, ring.radius, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore();
  }
}